import { useLocation, Navigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import CountryNav from '../components/CountryNav';
import CountryFooter from '../components/CountryFooter';
import Hero from '../components/Hero';
import EditorialMarquee from '../components/EditorialMarquee';
import { Bottleneck, Workflow, Manifesto, Confidentiality, ForeignLawyers, TrustSignals } from '../components/Sections';
import ClaimForm from '../components/ClaimForm';
import { COUNTRIES } from '../data/countries';
import { ARTICLES_BY_SLUG } from '../data/articles';
import { NATIVE_BY_COUNTRY } from './NativeLanding';

const PRESET_MAP = {
  uae: 'UAE', usa: 'USA', uk: 'UK', germany: 'Germany', greece: 'Greece',
  spain: 'Spain', france: 'France', switzerland: 'Switzerland', netherlands: 'Netherlands',
};

const ARTICLE_BY_COUNTRY = {
  uae: 'recovering-unpaid-invoices-italy-uae-businesses',
  usa: 'recognition-us-judgments-italy',
  uk: 'enforcing-uk-judgment-italy-after-brexit',
  germany: 'european-order-for-payment-german-creditors-italy',
  greece: 'greek-creditors-italian-debtors-epo-eapo',
  spain: 'recovering-debt-italy-spanish-creditors',
  france: 'enforcing-french-judgment-italy-injonction-de-payer',
  switzerland: 'lugano-convention-swiss-creditors-italy',
  netherlands: 'enforcing-dutch-judgment-italy-brussels-I-bis',
};

export default function CountryPage() {
  const { pathname } = useLocation();
  const code = pathname.replace('/', '').toLowerCase();
  const data = COUNTRIES[code];

  if (!data) return <Navigate to="/" replace />;

  const art = ARTICLES_BY_SLUG[ARTICLE_BY_COUNTRY[data.code]];

  const seoTitle = `Debt Recovery in Italy for ${data.label} Businesses | Studio Legale Belliardo`;
  const seoDesc = `Italian debt recovery for ${data.label} enterprises. Verona-based Italian law firm. An Italian lawyer on your side in Italy.`;
  const native = NATIVE_BY_COUNTRY[data.code];

  return (
    <div className="relative" data-testid={`country-page-${data.code}`}>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
        <link rel="canonical" href={`https://belliardo-international.com/${data.code}`} />
        <link rel="alternate" hrefLang="en" href={`https://belliardo-international.com/${data.code}`} />
        {native && <link rel="alternate" hrefLang={native.lang} href={`https://belliardo-international.com${native.path}`} />}
      </Helmet>
      <CountryNav country={data} />
      <Hero country={data} />
      <EditorialMarquee />
      <Bottleneck body={data.bottleneck} />
      <Workflow />
      <Manifesto />
      <Confidentiality />
      <TrustSignals trust={data.trust} accent={data.accent} />
      {art && (
        <section className="relative bg-ink-950 border-t border-white/10 py-20 md:py-24" data-testid="country-journal-section">
          <div className="mx-auto max-w-[1400px] px-6 md:px-10">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mb-6">— From the Journal</p>
            <Link to={`/articles/${art.slug}`} data-testid={`country-journal-link-${data.code}`} className="group block max-w-[820px]">
              <h2 className="font-serif text-3xl md:text-4xl text-white font-light leading-tight mb-4 group-hover:text-white/70 transition-colors">{art.title}</h2>
              <p className="text-white/60 text-base leading-relaxed mb-6">{art.subtitle}</p>
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/50 group-hover:text-white transition-colors">Read the note →</span>
            </Link>
          </div>
        </section>
      )}
      <ForeignLawyers />
      <ClaimForm presetCountry={PRESET_MAP[data.code]} />
      <CountryFooter country={data} />
    </div>
  );
}
